import { Search, ArrowUpDown, SlidersHorizontal, LayoutGrid, List } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CATEGORY_META, CATEGORY_ORDER } from "./category-meta";
import type { CategoryFilter } from "./HelpCategoryTabs";

export type SortOrder = "newest" | "oldest";
export type HelpView = "grid" | "list";

export function HelpToolbar({
  query,
  onQueryChange,
  category,
  onCategoryChange,
  sort,
  onSortChange,
  view,
  onViewChange,
}: {
  query: string;
  onQueryChange: (next: string) => void;
  category: CategoryFilter;
  onCategoryChange: (next: CategoryFilter) => void;
  sort: SortOrder;
  onSortChange: (next: SortOrder) => void;
  view: HelpView;
  onViewChange: (next: HelpView) => void;
}) {
  const { t } = useLingui();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="relative w-[18.75rem] max-w-full">
        <Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-(--gray-400)" />
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t`Search`}
          aria-label={t`Search articles`}
          className="h-10 rounded-lg border-(--shell-border) bg-(--color-surface-page) pl-11 shadow-(--shell-shadow)"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="text-base text-(--gray-500)">{t`Category:`}</span>
          <Select
            value={category}
            onValueChange={(next) => onCategoryChange(next as CategoryFilter)}
          >
            <SelectTrigger className="h-10 w-fit gap-1 border-none bg-transparent px-1 font-medium shadow-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All`}</SelectItem>
              {CATEGORY_ORDER.map((id) => (
                <SelectItem key={id} value={id}>
                  {t(CATEGORY_META[id].label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="brand-outline"
          size="sm"
          className="h-10"
          onClick={() => onSortChange(sort === "newest" ? "oldest" : "newest")}
        >
          <ArrowUpDown className="size-5" />
          {sort === "newest" ? t`Newest` : t`Oldest`}
        </Button>

        <Button
          variant="brand-outline"
          size="sm"
          className="h-10"
          onClick={() => toast.info(t`Filters are coming soon.`)}
        >
          <SlidersHorizontal className="size-5" />
          {t`Filter`}
        </Button>

        <div className="flex h-10 items-center gap-1 rounded-md bg-(--gray-100) p-1">
          <ViewButton
            active={view === "grid"}
            label={t`Grid view`}
            onClick={() => onViewChange("grid")}
          >
            <LayoutGrid className="size-5" />
          </ViewButton>
          <ViewButton
            active={view === "list"}
            label={t`List view`}
            onClick={() => onViewChange("list")}
          >
            <List className="size-5" />
          </ViewButton>
        </div>
      </div>
    </div>
  );
}

function ViewButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "flex h-full items-center justify-center rounded-md px-2 text-(--gray-500) transition-colors",
        active && "bg-(--color-surface-page) text-(--color-text-primary) shadow-sm",
      )}
    >
      {children}
    </button>
  );
}
